import React, { useContext, useEffect, useMemo } from 'react';
import { useLocale } from 'next-intl';
import { AnimatePresence, motion } from 'framer-motion';
import PropTypes from 'prop-types';
import { Configure, InstantSearch } from 'react-instantsearch';
import TypesenseInstantsearchAdapter from 'typesense-instantsearch-adapter';

import ArchiveBackgroundIcon from '@components/icons/archive-background/archiveBackground';

import ViewSwitch from './components/viewSwitch/viewSwitch';
import StackView from './components/stackView/stackView';
import ListView from './components/listView/listView';
import Transition from './components/transition/transition';

import ArchiveProvider, { Context } from './context/provider';
import StyledArchiveContent from './archive.style';
import Filter from './components/filter/filter';

const typesenseInstantSearchAdapter = new TypesenseInstantsearchAdapter({
  server: {
    apiKey: process.env.NEXT_PUBLIC_TYPESENSE_API_KEY,
    nodes: [
      {
        host: process.env.NEXT_PUBLIC_TYPESENSE_HOST,
        port: process.env.NEXT_PUBLIC_TYPESENSE_PORT,
        protocol: process.env.NEXT_PUBLIC_TYPESENSE_PROTOCOL
      }
    ]
  },
  additionalSearchParameters: {
    query_by: 'title,headline,subline,introCopy',
    query_by_weights: '6,5,3,1',
    sortBy: 'post_date_timestamp:desc'
  }
});

const ArchiveContent = ({ data }) => {
  const { view, resetFilter, isTransitionActive, updateScrollPosition } = useContext(Context);

  useEffect(() => {
    updateScrollPosition(0);
    resetFilter();
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [view]);

  const filter = useMemo(() => <Filter searchTerms={data.searchTerms} />, [data.searchTerms]);

  return (
    <>
      <StyledArchiveContent>
        <motion.div
          className="archive__switch"
          initial={{ opacity: 0 }}
          animate={{ opacity: isTransitionActive ? 0 : 1 }}
          transition={{ delay: 1.8, duration: 0.4, ease: 'linear' }}
        >
          <ViewSwitch />
        </motion.div>

        <AnimatePresence>
          {view === 'list' && !isTransitionActive && <ListView filter={filter} />}
          {view === 'stack' && !isTransitionActive && (
            <StackView introCard={data.introCard} filter={filter} />
          )}

          {isTransitionActive && <Transition introCard={data.introCard} />}
        </AnimatePresence>

        <motion.div
          className="archive__background"
          initial={{ opacity: 0 }}
          animate={{ opacity: isTransitionActive ? 0 : 1 }}
          transition={{ delay: 1.8, duration: 0.4, ease: 'linear' }}
        >
          <ArchiveBackgroundIcon className={`archive__background--${view}`} />
        </motion.div>
      </StyledArchiveContent>
    </>
  );
};

ArchiveContent.propTypes = {
  data: PropTypes.shape().isRequired
};

const Archive = ({ data }) => {
  const locale = useLocale();
  return (
    <InstantSearch
      indexName={`futurium_archive_${locale}`}
      searchClient={typesenseInstantSearchAdapter.searchClient}
    >
      <ArchiveProvider>
        <ArchiveContent data={data} />
      </ArchiveProvider>
      <Configure hitsPerPage={30} />
    </InstantSearch>
  );
};

Archive.propTypes = {
  data: PropTypes.shape()
};

export default Archive;
